import { FC, useEffect, useRef } from 'react';
import { translate } from '../../i18n';
import { isDesktop } from '../../platform';
import { AddPaymentMethodCard } from '../AddPaymentMethodCard';
import { MercadoPagoCard } from '../MercadoPagoCard';
import { PaymentMethodCard } from '../PaymentMethodCard';
import { paymentMethodItemId, type PaymentMethodItem } from './types';
import { paymentMethodCarouselStyles } from './styles';
import arrowRight from '../../assets/icons/arrowRight.svg';

export interface PaymentMethodCarouselProps {
  items: PaymentMethodItem[];
  selectedId?: string;
  onSelect: (item: PaymentMethodItem) => void;
  onAddPress: () => void;
  /** Eliminar una tarjeta: sin este manejador no se muestra la papelera. */
  onRemove?: (item: PaymentMethodItem) => void;
  /** Tarjeta en una línea, con menos datos. */
  compact?: boolean;
  /** La primera tarjeta de un pago en 2 no muestra el estado seleccionado. */
  highlightSelection?: boolean;
  testID?: string;
  /** Sin margen negativo: el scroll queda dentro del ancho del padre. */
  contained?: boolean;
}

/**
 * PaymentMethodCarousel — carrusel horizontal con los métodos de pago
 *
 * Cambios:
 * ✅ Estilos centralizados en styles.ts
 * ✅ i18n para hint text
 * ✅ Cero Tailwind, cero hardcodes
 */
export const PaymentMethodCarousel: FC<PaymentMethodCarouselProps> = ({
  items,
  selectedId,
  onSelect,
  onAddPress,
  onRemove,
  compact = false,
  highlightSelection = true,
  testID = 'payment-method-carousel',
  contained = false,
}) => {
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const dragRef = useRef({ active: false, x: 0, left: 0, moved: false });
  /** Última selección hecha por la propia persona (toque o scroll): no se vuelve a centrar sola. */
  const userSelectedIdRef = useRef<string | undefined>(undefined);

  /**
   * Si la selección cambia desde afuera (p. ej. se agregó una tarjeta y queda seleccionada), el carrusel
   * se desplaza hasta ella. Sin esto, el centrado automático del scroll volvía a elegir la primera.
   */
  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container || !selectedId || selectedId === userSelectedIdRef.current) {
      return;
    }
    const index = items.findIndex((item) => paymentMethodItemId(item) === selectedId);
    const element = index >= 0 ? (container.children[index] as HTMLElement | undefined) : undefined;
    if (!element) {
      return;
    }
    const target = element.offsetLeft + element.offsetWidth / 2 - container.clientWidth / 2;
    if (Math.abs(container.scrollLeft - target) >= 2) {
      container.scrollTo({ left: Math.max(0, target), behavior: 'smooth' });
    }
  }, [selectedId, items]);

  /**
   * Detecta cuándo el scroll se detiene y selecciona automáticamente la tarjeta centrada
   */
  const handleScroll = () => {
    if (!scrollContainerRef.current) return;

    const container = scrollContainerRef.current;
    const containerCenter = container.scrollLeft + container.clientWidth / 2;

    // Encontrar el item más cercano al centro
    let closestItem = null;
    let closestDistance = Infinity;

    items.forEach((item, index) => {
      const itemElement = container.children[index] as HTMLElement;
      if (!itemElement) return;

      const itemCenter = itemElement.offsetLeft + itemElement.offsetWidth / 2;
      const distance = Math.abs(itemCenter - containerCenter);

      if (distance < closestDistance) {
        closestDistance = distance;
        closestItem = item;
      }
    });

    // Seleccionar automáticamente el item centrado
    if (closestItem) {
      userSelectedIdRef.current = paymentMethodItemId(closestItem);
      onSelect(closestItem);
    }
  };

  // Encontrar el item seleccionado
  const selectedItem = items.find((item) => selectedId === paymentMethodItemId(item));

  /**
   * Sin selección (p. ej. tras recargar) el primer item es el que está a la vista, así que se muestra
   * con el alto de una tarjeta activa en vez del colapsado.
   */
  const activeId = selectedItem ? selectedId : items[0] ? paymentMethodItemId(items[0]) : undefined;

  // Mostrar hint solo bajo Mercado Pago o AddPaymentMethodCard
  const showHint =
    items.length > 1 &&
    (isDesktop || selectedItem?.kind === 'mercadopago' || selectedItem?.kind === 'add');

  return (
    <div
      style={contained ? paymentMethodCarouselStyles.contained : paymentMethodCarouselStyles.container}
      data-testid={testID}
    >
      <div
        ref={scrollContainerRef}
        onScroll={handleScroll}
        className={contained ? 'payment-carousel-contained' : undefined}
        style={
          contained
            ? paymentMethodCarouselStyles.containedScroll
            : paymentMethodCarouselStyles.scrollContainer
        }
        onPointerDown={
          contained
            ? (event) => {
                if (event.pointerType === 'touch') return;
                const el = event.currentTarget;
                dragRef.current = { active: true, x: event.clientX, left: el.scrollLeft, moved: false };
                el.setPointerCapture(event.pointerId);
              }
            : undefined
        }
        onPointerMove={
          contained
            ? (event) => {
                const drag = dragRef.current;
                if (!drag.active) return;
                const delta = event.clientX - drag.x;
                if (Math.abs(delta) > 4) drag.moved = true;
                event.currentTarget.scrollLeft = drag.left - delta;
              }
            : undefined
        }
        onPointerUp={
          contained
            ? () => {
                dragRef.current.active = false;
              }
            : undefined
        }
        onClickCapture={
          contained
            ? (event) => {
                if (!dragRef.current.moved) return;
                event.preventDefault();
                event.stopPropagation();
                dragRef.current.moved = false;
              }
            : undefined
        }
      >
        {items.map((item) => {
          const id = paymentMethodItemId(item);
          const isSelected = activeId === id;

          return (
            <div
              key={id}
              style={
                contained
                  ? paymentMethodCarouselStyles.containedItem
                  : paymentMethodCarouselStyles.carouselItem
              }
              data-testid={`carousel-item-${id}`}
            >
              {item.kind === 'add' && (
                <AddPaymentMethodCard
                  isSelected={isSelected}
                  onPress={onAddPress}
                  testID={`${id}-add`}
                />
              )}

              {item.kind === 'card' && (
                <PaymentMethodCard
                  issuerName={item.issuerName}
                  last4={item.last4}
                  holderName={item.holderName}
                  expiry={item.expiry}
                  network={item.network}
                  networkVariant={item.networkVariant}
                  gradientColors={item.gradientColors}
                  isSelected={highlightSelection && isSelected}
                  compact={compact}
                  onPress={() => {
                    userSelectedIdRef.current = id;
                    onSelect(item);
                  }}
                  onRemove={onRemove ? () => onRemove(item) : undefined}
                  testID={`${id}-card`}
                  accessibilityLabel={`${item.issuerName} tarjeta terminada en ${item.last4}`}
                />
              )}

              {item.kind === 'mercadopago' && (
                <MercadoPagoCard
                  isSelected={isSelected}
                  onPress={() => {
                    userSelectedIdRef.current = id;
                    onSelect(item);
                  }}
                  testID={`${id}-mercadopago`}
                />
              )}
            </div>
          );
        })}
      </div>

      {showHint && (
        <p style={paymentMethodCarouselStyles.hint} data-testid={`${testID}-hint`}>
          {translate(isDesktop ? 'paymentsUI.carousel.scrollHintWeb' : 'paymentsUI.carousel.scrollHint')}
          { isDesktop && <img src={arrowRight} alt="" style={paymentMethodCarouselStyles.arrow} /> }
        </p>
      )}
    </div>
  );
};

export type { PaymentMethodItem } from './types';
export { ADD_PAYMENT_METHOD_ID, paymentMethodItemId } from './types';
